import React, { useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigation } from '../context/NavigationContext';
import { AnimatePresence, motion } from 'framer-motion';

// Import components
import Hero from '../components/Hero';
import Letter from '../components/Letter';
import QuotesSlider from '../components/QuotesSlider';
import ImageSlider from '../components/ImageSlider';
import LoveList from '../components/LoveList';
import Timeline from '../components/Timeline';
import NicknameFlipCards from '../components/NicknameFlipCards';
import Surprise from '../components/Surprise';
import VideoSection from '../components/VideoSection';
import Footer from '../components/Footer';
import NavigationControls from '../components/NavigationControls';
import JourneyHeader from '../components/JourneyHeader';

const HomeScreen: React.FC = () => {
    const { toggleTheme } = useTheme();
    const { language, setLanguage, t } = useLanguage(); // 't' is used, not removed as per instruction's example, to maintain functionality
    const navigation = useNavigation();

    const sections = useMemo(() => [
        {
            id: 'hero',
            title: t('home.title') || '50 Days of Love',
            icon: '💖',
            component: <Hero />,
        },
        {
            id: 'letter',
            title: t('home.letterTitle') || 'My Love Letter',
            icon: '💌',
            component: <Letter />,
        },
        {
            id: 'quotes',
            title: t('home.quotesTitle') || 'Love Quotes',
            icon: '📜',
            component: <QuotesSlider />,
        },
        {
            id: 'images',
            title: t('home.photosTitle') || 'Our Journey in Photos',
            icon: '📸',
            component: <ImageSlider />,
        },
        {
            id: 'loveList',
            title: t('home.thingsTitle') || '50 Things I Love About You',
            icon: '💝',
            component: <LoveList />,
        },
        {
            id: 'timeline',
            title: t('home.timelineTitle') || 'Our Timeline',
            icon: '📅',
            component: <Timeline />,
        },
        {
            id: 'nicknames',
            title: t('home.nicknamesTitle') || 'Special Names For You',
            icon: '✨',
            component: <NicknameFlipCards />,
        },
        {
            id: 'surprise',
            title: t('home.surpriseTitle') || 'A Special Surprise',
            icon: '🎁',
            component: <Surprise onReveal={() => { }} />,
        },
        {
            id: 'videos',
            title: t('home.videoTitle') || 'Our Special Memory',
            icon: '🎬',
            component: (
                <VideoSection
                    title={t('home.videoTitle') || 'Our Special Memory'}
                    subtitle={t('home.videoSubtitle') || 'A heartfelt video message just for you'}
                    videoSource="https://assets.mixkit.co/videos/preview/mixkit-couple-in-love-walking-in-a-park-mid-shot-4444-large.mp4"
                />
            ),
        },
        {
            id: 'footer',
            title: t('home.finalTitle') || 'Forever Together',
            icon: '💫',
            component: <Footer />,
        },
    ], [t]);

    const restartJourney = () => {
        navigation.goToSection(0);
    };

    const handleNext = () => {
        if (navigation.currentSection < sections.length - 1) {
            navigation.goToNext();
        }
    };

    const handlePrevious = () => {
        if (navigation.currentSection > 0) {
            navigation.goToPrevious();
        }
    };

    return (
        <div className="flex flex-col min-h-screen max-w-6xl mx-auto shadow-2xl bg-white dark:bg-romantic-dark relative">
            {/* Header */}
            <JourneyHeader
                currentSection={navigation.currentSection}
                totalSections={sections.length}
                section={sections[navigation.currentSection]}
                onThemeToggle={toggleTheme}
                onLanguageToggle={() => setLanguage(language === 'en' ? 'ta' : 'en')}
                language={language}
            />

            {/* Main Content Area */}
            <main className="flex-1 mt-16 mb-20 overflow-x-hidden">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={navigation.currentSection}
                        initial={{ opacity: 0, x: navigation.animationDirection === 'left' ? 100 : -100 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: navigation.animationDirection === 'left' ? -100 : 100 }}
                        transition={{ type: 'spring', damping: 20, stiffness: 100 }}
                        className="w-full"
                    >
                        {sections[navigation.currentSection].component}
                    </motion.div>
                </AnimatePresence>
            </main>

            {/* Footer Controls */}
            <NavigationControls
                onNext={handleNext}
                onPrevious={handlePrevious}
                onRestart={restartJourney}
                currentIndex={navigation.currentSection}
                totalSections={sections.length}
                isFirst={navigation.isFirstSection}
                isLast={navigation.isLastSection}
            />
        </div>
    );
};

export default HomeScreen;
